import { Building2, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type { Department } from '../types'
import { AvatarStack, StatusBadge } from './AccessBits'

export type DepartmentRow = Department

type Props = {
  rows: DepartmentRow[]
  loading: boolean
  selectedId: string | null
  canWrite: boolean
  onSelect: (department: Department) => void
  onEdit: (department: Department) => void
  onDelete: (department: Department) => void
}

function DepartmentIcon() {
  return (
    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-blue/10 text-brand-blue">
      <Building2 className="size-4" />
    </span>
  )
}

export function DepartmentsTable({ rows, loading, selectedId, canWrite, onSelect, onEdit, onDelete }: Props) {
  const { t } = useTranslation()

  if (loading) {
    return (
      <div className="space-y-2 p-3">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-12" />
        ))}
      </div>
    )
  }

  return (
    <>
      <ul className="divide-y md:hidden">
        {rows.map((d) => (
          <li key={d.id} className={cn('space-y-3 p-3', selectedId === d.id && 'bg-brand-blue/[0.04]')} onClick={() => onSelect(d)}>
            <div className="flex items-start gap-3">
              <DepartmentIcon />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{d.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  <span className="font-mono">{d.code}</span>
                  {d.parent ? ` · ${d.parent.name}` : ''}
                </div>
              </div>
              <StatusBadge status={d.status} />
            </div>
            <div className="pl-12">
              <AvatarStack people={d.members.map((m) => ({ id: m.id, name: m.name, src: m.avatarUrl }))} />
            </div>
          </li>
        ))}
      </ul>

      <div className="hidden md:block">
        <Table>
          <THead>
            <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
              <TH>{t('access.departments.columns.name')}</TH>
              <TH className="hidden lg:table-cell">{t('access.departments.columns.parent')}</TH>
              <TH>{t('access.departments.columns.manager')}</TH>
              <TH className="text-right">{t('access.departments.columns.members')}</TH>
              <TH>{t('access.departments.columns.people')}</TH>
              <TH>{t('access.departments.columns.status')}</TH>
              {canWrite ? <TH className="w-12 text-right">{t('access.departments.columns.actions')}</TH> : null}
            </TR>
          </THead>
          <TBody>
            {rows.map((d) => (
              <TR key={d.id} className={cn('cursor-pointer', selectedId === d.id && 'bg-brand-blue/[0.04]')} onClick={() => onSelect(d)}>
                <TD>
                  <div className="flex items-center gap-3">
                    <DepartmentIcon />
                    <div className="min-w-0">
                      <p className="font-medium">{d.name}</p>
                      <p className="max-w-64 truncate text-xs text-muted-foreground">
                        <span className="font-mono">{d.code}</span>
                        {d.description ? ` · ${d.description}` : ''}
                      </p>
                    </div>
                  </div>
                </TD>
                <TD className="hidden text-muted-foreground lg:table-cell">{d.parent?.name ?? '—'}</TD>
                <TD>
                  {d.manager ? (
                    <div className="flex items-center gap-2">
                      <Avatar name={d.manager.name} src={d.manager.avatarUrl} className="size-7" />
                      <div className="min-w-0">
                        <p className="whitespace-nowrap text-sm">{d.manager.name}</p>
                        {d.manager.email ? <p className="truncate text-xs text-muted-foreground">{d.manager.email}</p> : null}
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">{t('access.departments.form.managerNone')}</span>
                  )}
                </TD>
                <TD className="text-right tabular-nums">{d.memberCount}</TD>
                <TD>
                  <AvatarStack people={d.members.map((m) => ({ id: m.id, name: m.name, src: m.avatarUrl }))} max={4} />
                </TD>
                <TD>
                  <StatusBadge status={d.status} />
                </TD>
                {canWrite ? (
                  <TD className="text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost" className="size-8" aria-label={t('access.departments.columns.actions')}>
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => onEdit(d)}>
                          <Pencil />
                          {t('common.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive data-[highlighted]:bg-destructive/10 [&_svg]:text-destructive" onSelect={() => onDelete(d)}>
                          <Trash2 />
                          {t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TD>
                ) : null}
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </>
  )
}
